import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getCurrentUser, updateUserProfile, changePassword, checkSlugUniqueness, cleanupDuplicateSlugs } from '../../services/api';
import { storage } from '../../firebase/config';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import PlayerForm from '../../components/player/PlayerForm';
import toast from 'react-hot-toast';
import { useTheme } from '../../context/ThemeContext';

const Settings = () => {
    const { theme, toggleTheme } = useTheme();
    const { data, isLoading } = useQuery({
        queryKey: ['me'],
        queryFn: getCurrentUser
    });

    const user = data || {};
    const [formData, setFormData] = useState({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        phone: user.phone || '',
        slug: user.slug || '',
        brandColor: user.brandColor || '#72c02c',
        logoUrl: user.logoUrl || '',
        bannerUrl: user.bannerUrl || '',
        welcomeMessage: user.welcomeMessage || ''
    });

    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [uploadingBanner, setUploadingBanner] = useState(false);

    // Password State
    const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [showPassword, setShowPassword] = useState(false);

    // Sync form when data loads
    React.useEffect(() => {
        const userId = user.uid || user.id;
        console.log('Settings.useEffect: user=', user, 'userId=', userId);
        if (userId) {
            setFormData({
                firstName: user.firstName || '',
                lastName: user.lastName || '',
                email: user.email || '',
                phone: user.phone || '',
                slug: user.slug || '',
                brandColor: user.brandColor || '#72c02c',
                logoUrl: user.logoUrl || '',
                bannerUrl: user.bannerUrl || '',
                welcomeMessage: user.welcomeMessage || ''
            });
        }
    }, [user]);

    const queryClient = useQueryClient();

    const mutation = useMutation({
        mutationFn: (newData) => {
            const userId = user.uid || user.id || localStorage.getItem('userId');
            if (!userId) throw new Error('No se ha podido identificar al usuario');
            return updateUserProfile(userId, newData);
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['me']);
            toast.success('Perfil actualizado con éxito');
        },
        onError: (error) => {
            console.error('mutation.onError: Error updating profile:', error);
            console.error('mutation.onError: Error details:', {
                message: error.message,
                code: error.code,
                stack: error.stack
            });
            alert('Error al actualizar el perfil: ' + (error.message || 'Desconocido'));
        }
    });

    const passwordMutation = useMutation({
        mutationFn: (data) => changePassword(data),
        onSuccess: () => {
            alert('Contraseña actualizada correctamente');
            setIsPasswordModalOpen(false);
            setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        },
        onError: (error) => {
            alert(error.response?.data?.message || 'Error al cambiar la contraseña');
        }
    });

    const resizeImage = (file, maxWidth, maxHeight) => {
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = (event) => {
                const img = new Image();
                img.src = event.target.result;
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    let width = img.width;
                    let height = img.height;

                    if (width > height) {
                        if (width > maxWidth) {
                            height *= maxWidth / width;
                            width = maxWidth;
                        }
                    } else {
                        if (height > maxHeight) {
                            width *= maxHeight / height;
                            height = maxHeight;
                        }
                    }

                    canvas.width = width;
                    canvas.height = height;

                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0, width, height);

                    canvas.toBlob((blob) => {
                        resolve(new File([blob], file.name, { type: 'image/jpeg', lastModified: Date.now() }));
                    }, 'image/jpeg', 0.8);
                };
            };
        });
    };

    const handleFileUpload = async (file, type) => {
        if (!file) return;
        
        const isLogo = type === 'logo';
        const setter = isLogo ? setUploadingLogo : setUploadingBanner;
        setter(true);

        try {
            // Redimensionar imagen en cliente antes de subir
            const resizedFile = await resizeImage(
                file, 
                isLogo ? 512 : 1200, 
                isLogo ? 512 : 600
            );

            const userId = user.uid || user.id || localStorage.getItem('userId');
            const storageRef = ref(storage, `organizers/${userId}/${type}_${Date.now()}_${file.name}`);
            const snapshot = await uploadBytes(storageRef, resizedFile);
            const downloadURL = await getDownloadURL(snapshot.ref);
            
            // Actualizamos el estado local
            const fieldName = isLogo ? 'logoUrl' : 'bannerUrl';
            setFormData(prev => ({
                ...prev,
                [fieldName]: downloadURL
            }));

            // GUARDADO AUTOMÁTICO: Lo guardamos directamente en la base de datos
            await updateUserProfile(userId, { [fieldName]: downloadURL });
            queryClient.invalidateQueries(['me']);
            
            toast.success(`${isLogo ? 'Logo' : 'Banner'} subido y guardado correctamente`);
        } catch (error) {
            console.error('Error uploading file:', error);
            toast.error('Error al subir la imagen');
        } finally {
            setter(false);
        }
    };

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();
        
        const userId = user.uid || user.id || localStorage.getItem('userId');
        
        // Si el slug ha cambiado, verificar su unicidad
        if (formData.slug && formData.slug !== user.slug) {
            const isUnique = await checkSlugUniqueness(formData.slug, userId);
            if (!isUnique) {
                toast.error('Este URL (slug) ya está en uso por otro organizador.');
                return;
            }
        }

        console.log('DEBUG: handleSubmit called with', formData);
        mutation.mutate(formData);
    };

    const [isCleaning, setIsCleaning] = useState(false);
    const handleCleanupSlugs = async () => {
        if (!user.slug) return;
        if (!window.confirm(`¿Limpiar duplicados de "${user.slug}"? Multiples cuentas detectadas.`)) return;
        
        setIsCleaning(true);
        try {
            const userId = user.uid || user.id || localStorage.getItem('userId');
            const count = await cleanupDuplicateSlugs(user.slug, userId);
            toast.success(`Se han limpiado ${count} cuentas duplicadas.`);
            queryClient.invalidateQueries(['me']);
        } catch (error) {
            console.error('Error cleaning slugs:', error);
            toast.error('Error al limpiar duplicados');
        } finally {
            setIsCleaning(false);
        }
    };

    const handlePasswordSubmit = (e) => {
        e.preventDefault();
        if (passwordData.newPassword !== passwordData.confirmPassword) {
            alert('Las contraseñas no coinciden');
            return;
        }
        passwordMutation.mutate({
            currentPassword: passwordData.currentPassword,
            newPassword: passwordData.newPassword
        });
    };

    if (isLoading) return <div className="p-4 sm:p-8 animate-pulse text-gray-400">Cargando configuración...</div>;

    return (
        <div className="max-w-4xl animate-fade-in relative">
            <h1 className="text-2xl sm:text-3xl font-black text-gray-900 dark:text-white mb-1 sm:mb-2 transition-colors">Configuración</h1>
            <p className="text-gray-500 dark:text-gray-400 text-sm sm:text-base mb-6 sm:mb-10">Gestiona tus datos personales y preferencias.</p>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-12">
                <div className="lg:col-span-2 space-y-8">
                    {/* Ficha de Jugador Reutilizable */}
                    <div className="bg-white dark:bg-slate-800 rounded-2xl lg:rounded-[2.5rem] border border-gray-100 dark:border-slate-700 shadow-sm p-6 sm:p-8 lg:p-10 transition-colors">
                        <PlayerForm 
                            initialData={user} 
                            onSubmit={(data) => mutation.mutate(data)} 
                            isSubmitting={mutation.isPending} 
                        />
                    </div>

                    {/* Whitelabel Settings (Solo para Organizadores) */}
                    {user.role === 'organizer' && (
                        <div className="bg-white dark:bg-slate-800 rounded-2xl lg:rounded-[2.5rem] border border-gray-100 dark:border-slate-700 shadow-sm p-6 sm:p-8 lg:p-10 transition-colors">
                            <div className="mb-8">
                                <h3 className="text-xl font-black text-gray-900 dark:text-white mb-1">Marca (Whitelabel)</h3>
                                <p className="text-sm text-gray-500 dark:text-gray-400">Personaliza tu micro-site para tus jugadores.</p>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Slug de URL</label>
                                        <div className="flex items-center">
                                            <span className="bg-gray-100 dark:bg-slate-900 border-2 border-r-0 border-gray-100 dark:border-slate-600 rounded-l-xl p-3 text-gray-400 font-bold text-xs">/o/</span>
                                            <input
                                                type="text"
                                                value={formData.slug}
                                                onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                                                className="w-full bg-gray-50 dark:bg-slate-700/50 border-2 border-gray-100 dark:border-slate-600 rounded-r-xl p-3 focus:bg-white dark:focus:bg-slate-600 focus:border-primary-500 dark:focus:border-primary-500 transition-all outline-none font-bold text-sm dark:text-white"
                                                placeholder="tu-marca-personal"
                                            />
                                        </div>
                                        {formData.slug && (
                                            <a 
                                                href={`/o/${formData.slug}`} 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                className="text-[10px] text-primary-600 font-bold hover:underline flex items-center gap-1 mt-1 ml-1"
                                            >
                                                🔗 Ver espacio público: /o/{formData.slug}
                                            </a>
                                        )}
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Color de Marca</label>
                                        <div className="flex gap-3 items-center">
                                            <input
                                                type="color"
                                                value={formData.brandColor}
                                                onChange={(e) => setFormData({ ...formData, brandColor: e.target.value })}
                                                className="w-12 h-12 rounded-xl cursor-pointer border-2 border-gray-100 p-1 flex-shrink-0"
                                            />
                                            <input
                                                type="text"
                                                value={formData.brandColor}
                                                onChange={(e) => setFormData({ ...formData, brandColor: e.target.value })}
                                                className="flex-1 bg-gray-50 border-2 border-gray-100 rounded-xl p-3 focus:bg-white focus:border-primary-500 transition-all outline-none font-bold uppercase text-sm"
                                                placeholder="#000000"
                                            />
                                        </div>
                                    </div>

                                    <div className="space-y-2 sm:col-span-2">
                                        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Logo del Organizador</label>
                                        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                                            <div 
                                                className="w-40 h-40 rounded-2xl bg-gray-50 dark:bg-slate-700/50 border-2 border-gray-100 dark:border-slate-600 flex items-center justify-center overflow-hidden flex-shrink-0"
                                                style={{ width: '160px', height: '160px' }}
                                            >
                                                {formData.logoUrl ? (
                                                    <img src={formData.logoUrl} alt="Logo" className="w-full h-full object-contain" />
                                                ) : (
                                                    <span className="text-4xl">🖼️</span>
                                                )}
                                            </div>
                                            <div className="flex-1 space-y-2">
                                                <div className="flex gap-2">
                                                    <label className={`cursor-pointer px-4 py-2 rounded-xl font-bold text-xs transition-all ${uploadingLogo ? 'bg-gray-100 text-gray-400' : 'bg-primary-50 text-primary-600 hover:bg-primary-100'}`}>
                                                        {uploadingLogo ? '⌛ Subiendo...' : '🔼 Subir Logo'}
                                                        <input 
                                                            type="file" 
                                                            className="hidden" 
                                                            accept="image/*"
                                                            onChange={(e) => handleFileUpload(e.target.files[0], 'logo')}
                                                            disabled={uploadingLogo}
                                                        />
                                                    </label>
                                                    {formData.logoUrl && (
                                                        <button 
                                                            type="button"
                                                            onClick={() => setFormData({...formData, logoUrl: ''})}
                                                            className="px-4 py-2 rounded-xl font-bold text-xs bg-red-50 text-red-600 hover:bg-red-100"
                                                        >
                                                            Eliminar
                                                        </button>
                                                    )}
                                                </div>
                                                <p className="text-[10px] text-gray-400 leading-tight">
                                                    Optimizado automáticamente. Máximo 512x512px.
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="space-y-2 sm:col-span-2">
                                        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Banner del Espacio comercial</label>
                                        <div className="space-y-4">
                                            <div 
                                                className="w-full h-20 rounded-2xl bg-gray-50 border-2 border-gray-100 flex items-center justify-center overflow-hidden relative"
                                                style={{ height: '80px' }}
                                            >
                                                {formData.bannerUrl ? (
                                                    <img src={formData.bannerUrl} alt="Banner" className="w-full h-full object-cover" />
                                                ) : (
                                                    <div className="text-center">
                                                        <span className="text-2xl block mb-1">🌄</span>
                                                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Sin Banner</span>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                                                <div className="flex gap-2">
                                                    <label className={`cursor-pointer px-4 py-2 rounded-xl font-bold text-xs transition-all ${uploadingBanner ? 'bg-gray-100 text-gray-400' : 'bg-primary-50 text-primary-600 hover:bg-primary-100'}`}>
                                                        {uploadingBanner ? '⌛ Subiendo...' : '🔼 Subir Banner'}
                                                        <input 
                                                            type="file" 
                                                            className="hidden" 
                                                            accept="image/*"
                                                            onChange={(e) => handleFileUpload(e.target.files[0], 'banner')}
                                                            disabled={uploadingBanner}
                                                        />
                                                    </label>
                                                    {formData.bannerUrl && (
                                                        <button 
                                                            type="button"
                                                            onClick={() => setFormData({...formData, bannerUrl: ''})}
                                                            className="px-4 py-2 rounded-xl font-bold text-xs bg-red-50 text-red-600 hover:bg-red-100"
                                                        >
                                                            Eliminar
                                                        </button>
                                                    )}
                                                </div>
                                                <p className="text-[10px] text-gray-400 leading-tight">
                                                    Optimizado automáticamente. Máximo 1200x600px.
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="space-y-2 sm:col-span-2">
                                        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Mensaje de Bienvenida</label>
                                        <textarea
                                            value={formData.welcomeMessage}
                                            onChange={(e) => setFormData({ ...formData, welcomeMessage: e.target.value })}
                                            className="w-full bg-gray-50 dark:bg-slate-700/50 border-2 border-gray-100 dark:border-slate-600 rounded-xl p-3 focus:bg-white dark:focus:bg-slate-600 focus:border-primary-500 transition-all outline-none font-bold min-h-[100px] text-sm dark:text-white"
                                            placeholder="Bienvenidos a mi club..."
                                        />
                                    </div>
                                </div>

                                <div className="pt-4 border-t border-gray-50 dark:border-slate-700">
                                    <button
                                        type="submit"
                                        disabled={mutation.isPending}
                                        className="w-full py-4 bg-slate-900 text-white font-black rounded-xl hover:bg-slate-800 transition-all transform hover:-translate-y-1 shadow-xl uppercase tracking-widest text-xs"
                                    >
                                        {mutation.isPending ? 'Guardando Marca...' : 'Guardar Configuración de Marca'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}
                </div>

                <div className="space-y-4 sm:space-y-6 lg:space-y-8">
                    {/* Apariencia */}
                    <div className="bg-white dark:bg-slate-800 rounded-2xl lg:rounded-[2.5rem] p-6 sm:p-8 border border-gray-100 dark:border-slate-700 shadow-sm">
                        <div className="text-2xl sm:text-3xl mb-3 sm:mb-4">{theme === 'dark' ? '🌙' : '☀️'}</div>
                        <h3 className="text-base sm:text-lg font-black text-gray-900 dark:text-white mb-1 sm:mb-2 uppercase tracking-tighter">Apariencia</h3>
                        <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 font-medium leading-relaxed mb-4 sm:mb-6">Elige el tema que prefieras para el dashboard.</p>
                        
                        <div className="flex bg-gray-100 dark:bg-slate-900 p-1.5 rounded-xl">
                            <button
                                onClick={() => theme === 'dark' && toggleTheme()}
                                className={`flex-1 py-2.5 px-4 rounded-lg text-xs font-black uppercase tracking-wider transition-all ${
                                    theme === 'light' 
                                    ? 'bg-white text-gray-900 shadow-sm' 
                                    : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
                                }`}
                            >
                                Claro
                            </button>
                            <button
                                onClick={() => theme === 'light' && toggleTheme()}
                                className={`flex-1 py-2.5 px-4 rounded-lg text-xs font-black uppercase tracking-wider transition-all ${
                                    theme === 'dark' 
                                    ? 'bg-slate-700 text-white shadow-sm' 
                                    : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
                                }`}
                            >
                                Oscuro
                            </button>
                        </div>
                    </div>

                    <div className="bg-primary-50 dark:bg-slate-800/80 rounded-2xl lg:rounded-[2.5rem] p-6 sm:p-8 border border-primary-100 dark:border-slate-700">
                        <div className="text-2xl sm:text-3xl mb-3 sm:mb-4">🔐</div>
                        <h3 className="text-base sm:text-lg font-black text-primary-900 dark:text-white mb-1 sm:mb-2 uppercase tracking-tighter">Seguridad</h3>
                        <p className="text-xs sm:text-sm text-primary-700 dark:text-gray-400 font-medium leading-relaxed mb-4 sm:mb-6">Actualiza tu contraseña regularmente.</p>
                        <button
                            onClick={() => setIsPasswordModalOpen(true)}
                            className="w-full py-2.5 sm:py-3 bg-white dark:bg-slate-700 text-primary-600 dark:text-white font-bold rounded-lg sm:rounded-xl border border-primary-200 dark:border-slate-600 hover:bg-primary-100 dark:hover:bg-slate-600 transition-colors text-xs uppercase"
                        >
                            Cambiar Contraseña
                        </button>
                    </div>

                    <div className="bg-amber-50 dark:bg-amber-900/10 rounded-2xl lg:rounded-[2.5rem] p-6 sm:p-8 border border-amber-100 dark:border-amber-900/20">
                        <div className="text-2xl sm:text-3xl mb-3 sm:mb-4">🧹</div>
                        <h3 className="text-base sm:text-lg font-black text-amber-900 dark:text-amber-100 mb-1 sm:mb-2 uppercase tracking-tighter">Limpieza de Datos</h3>
                        <p className="text-xs sm:text-sm text-amber-700 dark:text-amber-200/70 font-medium leading-relaxed mb-4 sm:mb-6">Elimina duplicados que interfieren con tu sitio público.</p>
                        <button
                            onClick={handleCleanupSlugs}
                            disabled={isCleaning}
                            className="w-full py-2.5 sm:py-3 bg-white dark:bg-amber-900/30 text-amber-600 dark:text-amber-200 font-bold rounded-lg sm:rounded-xl border border-amber-200 dark:border-amber-900/40 hover:bg-amber-100 dark:hover:bg-amber-900/50 transition-colors text-xs uppercase"
                        >
                            {isCleaning ? 'Limpiando...' : 'Limpiar Duplicados de URL'}
                        </button>
                    </div>

                    <div className="bg-red-50 dark:bg-red-900/10 rounded-2xl lg:rounded-[2.5rem] p-6 sm:p-8 border border-red-100 dark:border-red-900/20">
                        <div className="text-2xl sm:text-3xl mb-3 sm:mb-4">⚠️</div>
                        <h3 className="text-base sm:text-lg font-black text-red-900 dark:text-red-100 mb-1 sm:mb-2 uppercase tracking-tighter">Zona de Peligro</h3>
                        <p className="text-xs sm:text-sm text-red-700 dark:text-red-200/70 font-medium leading-relaxed mb-4 sm:mb-6">Eliminar tu cuenta es irreversible.</p>
                        <button className="w-full py-2.5 sm:py-3 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-300 font-bold rounded-lg sm:rounded-xl border border-red-200 dark:border-red-900/40 hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors text-xs uppercase">
                            Borrar Cuenta
                        </button>
                    </div>
                </div>
            </div>

            {/* Password Modal */}
            {isPasswordModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm animate-fade-in">
                    <div className="bg-white dark:bg-slate-800 rounded-2xl lg:rounded-[2.5rem] shadow-2xl w-full max-w-md overflow-hidden animate-slide-in-up p-6 sm:p-8 border border-gray-100 dark:border-slate-700">
                        <h3 className="text-xl sm:text-2xl font-black text-gray-900 dark:text-white mb-4 sm:mb-6">Cambiar Contraseña</h3>
                        <form onSubmit={handlePasswordSubmit} className="space-y-4 sm:space-y-6">
                            <div className="space-y-2">
                                <label className="text-xs font-black uppercase tracking-widest text-gray-400">Contraseña Actual</label>
                                <div className="relative">
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        required
                                        value={passwordData.currentPassword}
                                        onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                                        className="w-full bg-gray-50 dark:bg-slate-700/50 border-2 border-gray-100 dark:border-slate-600 rounded-xl p-3 outline-none focus:border-primary-500 dark:focus:border-primary-500 font-bold pr-12 dark:text-white transition-colors"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                                    >
                                        {showPassword ? (
                                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l18 18" />
                                            </svg>
                                        ) : (
                                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                            </svg>
                                        )}
                                    </button>
                                </div>
                            </div>
                            <div className="space-y-2">
                                <label className="text-xs font-black uppercase tracking-widest text-gray-400">Nueva Contraseña</label>
                                <div className="relative">
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        required
                                        minLength={6}
                                        value={passwordData.newPassword}
                                        onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                                        className="w-full bg-gray-50 dark:bg-slate-700/50 border-2 border-gray-100 dark:border-slate-600 rounded-xl p-3 outline-none focus:border-primary-500 dark:focus:border-primary-500 font-bold pr-12 dark:text-white transition-colors"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                                    >
                                        {showPassword ? (
                                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l18 18" />
                                            </svg>
                                        ) : (
                                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                            </svg>
                                        )}
                                    </button>
                                </div>
                            </div>
                            <div className="space-y-2">
                                <label className="text-xs font-black uppercase tracking-widest text-gray-400">Confirmar Nueva</label>
                                <div className="relative">
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        required
                                        minLength={6}
                                        value={passwordData.confirmPassword}
                                        onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                                        className="w-full bg-gray-50 dark:bg-slate-700/50 border-2 border-gray-100 dark:border-slate-600 rounded-xl p-3 outline-none focus:border-primary-500 dark:focus:border-primary-500 font-bold pr-12 dark:text-white transition-colors"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                                    >
                                        {showPassword ? (
                                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l18 18" />
                                            </svg>
                                        ) : (
                                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                            </svg>
                                        )}
                                    </button>
                                </div>
                            </div>
                            <div className="flex flex-col sm:flex-row gap-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => setIsPasswordModalOpen(false)}
                                    className="py-3 rounded-xl border border-gray-200 dark:border-slate-600 font-bold text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-700 order-2 sm:order-1 sm:flex-1 transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={passwordMutation.isPending}
                                    className="py-3 rounded-xl bg-primary-600 text-white font-bold hover:bg-primary-700 shadow-lg order-1 sm:order-2 sm:flex-1"
                                >
                                    {passwordMutation.isPending ? 'Guardando...' : 'Confirmar'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Settings;
